import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    ReferenceField,
    FunctionField,
    Pagination,
    useNotify,
    Title,
    Confirm,
    TopToolbar,
    ExportButton,
    downloadCSV,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { Card, CardContent, Tabs, Tab, Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { authenticatedApi } from "../dataProvider";
import { designations, getDesignations } from "../helpers/strings";

const designationLabel = (value) => designations[value] ?? designations['none'] ?? 'N/A';

const currentDesignationExporter = (communities) => {
    const dataForExport = communities.map(community => ({
        'Member Number': community.member_number,
        'Name': community.name,
        'City': community.city,
        'State': community.state,
        'Designation': getDesignations(community.designation ?? null) ?? community.designation,
        'Coordinating Program': community?.coordinating_program?.name ?? '',
    }));
    jsonExport(dataForExport, {
        headers: ['Member Number', 'Name', 'City', 'State', 'Designation', 'Coordinating Program'],
    }, (err, csv) => {
        downloadCSV(csv, 'current-designations');
    });
};

const designationHistoryExporter = (records) => {
    const dataForExport = records.map(record => ({
        'Community': record.community_name,
        'State': record.state ?? '',
        'Form': record.form?.name ?? record.form_id,
        'Designation': getDesignations(record.designation ?? null) ?? record.designation,
        'Archived At': record.archived_at ?? '',
    }));
    jsonExport(dataForExport, {
        headers: ['Community', 'State', 'Form', 'Designation', 'Archived At'],
    }, (err, csv) => {
        downloadCSV(csv, 'designation-history');
    });
};

const ExportOnlyActions = () => (
    <TopToolbar>
        <ExportButton maxResults={5000} />
    </TopToolbar>
);

// ---- This Year tab: current community designations + Archive action ----
const ThisYearTab = ({ status, reloadStatus }) => {
    const notify = useNotify();
    const [isArchiving, setIsArchiving] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const alreadyArchived = status?.is_archived;
    const activeFormName = status?.active_form?.name ?? 'the current form';

    const doArchive = () => {
        setIsArchiving(true);
        authenticatedApi.post('designations/archive')
            .then(() => {
                notify('Designations archived', { type: 'success' });
                reloadStatus();
            })
            .catch(e => {
                notify(e?.response?.data?.message ?? 'Could not archive designations', { type: 'error' });
            })
            .finally(() => {
                setIsArchiving(false);
                setConfirmOpen(false);
            });
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Archiving snapshots every community's current designation for <strong>{activeFormName}</strong>.
                {alreadyArchived && (
                    <> This form has already been archived — <strong>re-archiving will overwrite the existing archived data for this form.</strong></>
                )}
            </Alert>

            <Button
                variant="contained"
                color={alreadyArchived ? 'warning' : 'primary'}
                disabled={isArchiving}
                onClick={() => setConfirmOpen(true)}
                sx={{ mb: 2 }}
            >
                {isArchiving ? 'Archiving…' : (alreadyArchived ? 'Re-archive current designations' : 'Archive current designations')}
            </Button>

            <Confirm
                isOpen={confirmOpen}
                loading={isArchiving}
                title={alreadyArchived ? 'Re-archive designations?' : 'Archive designations?'}
                content={alreadyArchived
                    ? `${activeFormName} has already been archived. Re-archiving will overwrite that archived data. Continue?`
                    : `This will snapshot every community's current designation for ${activeFormName}.`}
                onConfirm={doArchive}
                onClose={() => setConfirmOpen(false)}
            />

            <List
                resource="communities"
                title=" "
                actions={<ExportOnlyActions />}
                exporter={currentDesignationExporter}
                pagination={<Pagination />}
                perPage={25}
                sort={{ field: 'state', order: 'ASC' }}
                storeKey={false}
                disableSyncWithLocation
            >
                <Datagrid bulkActionButtons={false} rowClick={false}>
                    <TextField source="name" label="Community" />
                    <TextField source="state" label="State" />
                    <FunctionField label="Current Designation" render={record => designationLabel(record.designation)} />
                </Datagrid>
            </List>
        </Box>
    );
};

// ---- History tab: paginated designation_history ----
const HistoryTab = () => {
    return (
        <List
            resource="designation-history"
            title=" "
            actions={<ExportOnlyActions />}
            exporter={designationHistoryExporter}
            pagination={<Pagination />}
            perPage={25}
            sort={{ field: 'community_name', order: 'ASC' }}
            storeKey={false}
            disableSyncWithLocation
        >
            <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="community_name" label="Community" />
                <TextField source="state" label="State" />
                <ReferenceField source="form_id" reference="forms" label="Form" link={false}>
                    <TextField source="name" />
                </ReferenceField>
                <FunctionField label="Designation" render={record => designationLabel(record.designation)} />
                <FunctionField label="Archived" render={record => record.archived_at ? new Date(record.archived_at).toLocaleDateString() : ''} />
            </Datagrid>
        </List>
    );
};

// ---- Clear tab: two-click reset, gated on archive existing ----
const ClearTab = ({ status, reloadStatus }) => {
    const notify = useNotify();
    const [firstClick, setFirstClick] = React.useState(false);
    const [isClearing, setIsClearing] = React.useState(false);

    const canClear = status?.is_archived;

    const doClear = () => {
        setIsClearing(true);
        authenticatedApi.post('designations/clear')
            .then(() => {
                notify('All designations cleared', { type: 'success' });
                setFirstClick(false);
                reloadStatus();
            })
            .catch(e => {
                notify(e?.response?.data?.message ?? 'Could not clear designations', { type: 'error' });
            })
            .finally(() => {
                setIsClearing(false);
            });
    };

    return (
        <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
                Designations must be <strong>archived first</strong> (on the This Year tab). Clearing should be done
                <strong> after the last form entries are finalized</strong> and <strong>before the new form is released</strong>.
                This resets every community's current designation to N/A.
            </Alert>

            {!canClear && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    The current designations have not been archived yet. Archive them before clearing.
                </Typography>
            )}

            {!firstClick ? (
                <Button
                    variant="contained"
                    color="error"
                    disabled={!canClear || isClearing}
                    onClick={() => setFirstClick(true)}
                >
                    Clear designation status for all communities
                </Button>
            ) : (
                <Button
                    variant="contained"
                    color="error"
                    disabled={isClearing}
                    onClick={doClear}
                >
                    {isClearing ? 'Clearing…' : 'Click again to reset all current designations'}
                </Button>
            )}
        </Box>
    );
};

const DesignationsPage = () => {
    const [tab, setTab] = React.useState(0);
    const [status, setStatus] = React.useState(null);
    const [loadingStatus, setLoadingStatus] = React.useState(true);

    const reloadStatus = React.useCallback(() => {
        setLoadingStatus(true);
        authenticatedApi.get('designations/status')
            .then(res => setStatus(res.data))
            .catch(() => setStatus(null))
            .finally(() => setLoadingStatus(false));
    }, []);

    React.useEffect(() => { reloadStatus(); }, [reloadStatus]);

    return (
        <Card sx={{ mt: 2 }}>
            <Title title="Designations" />
            <CardContent>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label="This Year" />
                    <Tab label="Designation History" />
                    <Tab label="Clear Designation" />
                </Tabs>

                {loadingStatus && <CircularProgress size={24} />}

                {!loadingStatus && (<>
                    {tab === 0 && <ThisYearTab status={status} reloadStatus={reloadStatus} />}
                    {tab === 1 && <HistoryTab />}
                    {tab === 2 && <ClearTab status={status} reloadStatus={reloadStatus} />}
                </>)}
            </CardContent>
        </Card>
    );
};

export { DesignationsPage };